import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo, { SITE_URL, buildLodgingSchema } from "../components/Seo";
import { useContent } from "../context/SiteContentContext";
import seoData from "../data/seoPages.json";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import SeoNotFound from "./SeoNotFound";

const COMMON_FAQS = [
  { q: "What are the check-in and check-out timings?", a: "Overnight stays check in at 8:00 PM and check out at 11:00 AM. Hourly stays run for the slot you book." },
  { q: "How do I book ROOM XIII?", a: "Choose your date and time on our booking page and pay securely online. You will receive a smart-lock code for self check-in." },
];

export default function SeoLandingPage() {
  const { slug } = useParams();
  const { get } = useContent();
  const page = seoData.pages.find((p) => p.slug === slug);
  if (!page) return <SeoNotFound />;

  const path = `/stays/${page.slug}`;
  const faqs = [...page.faqs, ...COMMON_FAQS];
  const related = seoData.pages.filter((p) => p.slug !== page.slug).slice(0, 6);

  const jsonLd = [
    buildLodgingSchema(get),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: page.keyword, item: `${SITE_URL}${path}` },
      ],
    },
  ];

  return (
    <div className="pt-32 pb-24 grain min-h-screen" data-testid="seo-landing-page">
      <Seo title={page.title} description={page.description} path={path} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <nav aria-label="Breadcrumb" className="text-xs text-white/40 mb-8">
          <Link to="/" className="hover:text-gold">Home</Link> <span className="mx-2">/</span> <span>{page.keyword}</span>
        </nav>
        <div className="text-center mb-14">
          <div className="eyebrow">ROOM XIII · Zirakpur</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-6">{page.h1}</h1>
          <div className="divider-gold mx-auto" />
          <p className="text-luxury-body mt-6 max-w-2xl mx-auto">{page.intro}</p>
          <Link to="/booking" className="inline-flex items-center gap-2 mt-8 px-8 py-3 border border-gold text-gold hover:bg-gold hover:text-black transition-colors text-xs uppercase tracking-[0.25em]">
            Book Now <ArrowRight size={14} />
          </Link>
        </div>

        <img src="/Images/suite1-hero.png" alt={`${page.keyword} – ROOM XIII suite`} loading="lazy" className="w-full mb-14 border border-white/5" />

        <h2 className="font-display text-2xl mb-6">Why choose ROOM XIII</h2>
        <ul className="grid sm:grid-cols-2 gap-4 mb-14">
          {page.highlights.map((h) => (
            <li key={h} className="glass border border-white/5 p-5 flex gap-3 text-luxury-body text-sm">
              <Check size={16} className="text-gold shrink-0 mt-0.5" /> {h}
            </li>
          ))}
        </ul>

        <h2 className="font-display text-2xl mb-6">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="space-y-3 mb-14">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="glass border border-white/5 px-6">
              <AccordionTrigger className="text-white text-left hover:no-underline py-5 font-display text-base">{f.q}</AccordionTrigger>
              <AccordionContent className="text-luxury-body pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <h2 className="font-display text-2xl mb-6">Explore more</h2>
        <ul className="flex flex-wrap gap-3 text-sm">
          {related.map((p) => (
            <li key={p.slug}>
              <Link to={`/stays/${p.slug}`} className="px-4 py-2 border border-white/10 text-white/60 hover:text-gold hover:border-gold transition-colors inline-block">
                {p.keyword}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
